import React, { useState, useEffect, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import SurveyTagManager from "../components/SurveyTagManager";
import api from "../services/api";

export default function TagSummaryDashboard() {
  const { user } = useAuth({ requiredRole: "manager" });

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [summary, setSummary] = useState(null);
  const [crossTab, setCrossTab] = useState(null);
  const [taggedContacts, setTaggedContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [selectedTag, setSelectedTag] = useState("");
  const [contactPage, setContactPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary"); // summary, contacts, manage

  // Load campaigns
  useEffect(() => {
    api.get("/campaigns").then((res) => setCampaigns(res.data.campaigns || [])).catch(() => {});
  }, []);

  // Load summary
  const loadSummary = useCallback(async () => {
    if (!selectedCampaign) return;
    setLoading(true);
    try {
      const [summaryRes, crossTabRes] = await Promise.all([
        api.get(`/survey-tags/campaigns/${selectedCampaign}/tag-summary`),
        api.get(`/survey-tags/campaigns/${selectedCampaign}/tag-cross-tab`),
      ]);
      setSummary(summaryRes.data);
      setCrossTab(crossTabRes.data);
    } catch (err) {
      console.error("Failed to load summary:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Load tagged contacts
  const loadContacts = useCallback(async () => {
    if (!selectedCampaign) return;
    try {
      const params = new URLSearchParams();
      params.append("page", contactPage);
      params.append("per_page", "50");
      if (selectedTag) params.append("tag_id", selectedTag);

      const res = await api.get(`/survey-tags/campaigns/${selectedCampaign}/tagged-contacts?${params.toString()}`);
      setTaggedContacts(res.data.contacts || []);
      setContactsTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to load contacts:", err);
    }
  }, [selectedCampaign, selectedTag, contactPage]);

  useEffect(() => {
    if (activeTab === "contacts") {
      loadContacts();
    }
  }, [activeTab, loadContacts]);

  // Export
  const handleExport = async () => {
    const params = selectedTag ? `?tag_id=${selectedTag}` : "";
    try {
      const res = await api.get(
        `/survey-tags/campaigns/${selectedCampaign}/tags/export${params}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `tags_export_${selectedCampaign}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Export failed");
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Survey Tags</h1>
        <div className="flex items-center space-x-2">
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={selectedCampaign}
            onChange={(e) => { setSelectedCampaign(e.target.value); setSummary(null); setCrossTab(null); }}
          >
            <option value="">Select campaign...</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {selectedCampaign && (
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            >
              Export CSV
            </button>
          )}
        </div>
      </div>

      {selectedCampaign && (
        <>
          {/* Tab bar */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
            {[
              { key: "summary", label: "Summary" },
              { key: "contacts", label: "Tagged Contacts" },
              { key: "manage", label: "Manage Tags" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Summary tab */}
          {activeTab === "summary" && summary && (
            <div className="space-y-4">
              {/* Overview cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">{summary.total_contacts}</p>
                  <p className="text-xs text-gray-400 mt-1">Total Contacts</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{summary.tagged_contacts}</p>
                  <p className="text-xs text-gray-400 mt-1">Tagged</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <p className="text-3xl font-bold text-gray-400">{summary.untagged_contacts}</p>
                  <p className="text-xs text-gray-400 mt-1">Untagged</p>
                </div>
              </div>

              {/* Tag distribution */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Tag Distribution</h3>
                <div className="space-y-3">
                  {summary.tags.map((tag) => (
                    <div key={tag.id}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                          <span className="text-sm font-medium text-gray-700">{tag.name}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {tag.count} ({tag.pct}%)
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${tag.pct}%`, backgroundColor: tag.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cross-tab */}
              {crossTab && crossTab.tags.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 overflow-x-auto">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Tags vs. Contact Status</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 pr-4 text-gray-500 font-medium">Tag</th>
                        {crossTab.statuses.map((s) => (
                          <th key={s} className="text-center py-2 px-3 text-gray-500 font-medium capitalize">{s.replace("_", " ")}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {crossTab.tags.map((tag) => (
                        <tr key={tag.id} className="border-b border-gray-50">
                          <td className="py-2 pr-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tag.color }} />
                              <span className="font-medium text-gray-700">{tag.name}</span>
                            </div>
                          </td>
                          {crossTab.statuses.map((s) => (
                            <td key={s} className="text-center py-2 px-3 text-gray-600">
                              {crossTab.cross_tab[tag.name]?.[s] || 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Contacts tab */}
          {activeTab === "contacts" && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <select
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  value={selectedTag}
                  onChange={(e) => { setSelectedTag(e.target.value); setContactPage(1); }}
                >
                  <option value="">All Tags</option>
                  {summary?.tags.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.count})</option>
                  ))}
                </select>
                <span className="text-sm text-gray-400">{contactsTotal} contacts</span>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left py-2 px-4 font-medium text-gray-500">Name</th>
                      <th className="text-left py-2 px-4 font-medium text-gray-500">Phone</th>
                      <th className="text-left py-2 px-4 font-medium text-gray-500">Status</th>
                      <th className="text-left py-2 px-4 font-medium text-gray-500">Tags</th>
                      <th className="text-left py-2 px-4 font-medium text-gray-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taggedContacts.map((item) => (
                      <tr key={item.campaign_contact_id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-4 font-medium text-gray-900">
                          {item.contact.first_name} {item.contact.last_name}
                        </td>
                        <td className="py-2 px-4 text-gray-500 font-mono text-xs">
                          {item.contact.phone_number}
                        </td>
                        <td className="py-2 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.status === "replied" ? "bg-green-100 text-green-700" :
                            item.status === "sent" ? "bg-blue-100 text-blue-700" :
                            item.status === "opted_out" ? "bg-red-100 text-red-700" :
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="py-2 px-4">
                          <div className="flex flex-wrap gap-1">
                            {item.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: tag.color + "20", color: tag.color }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 px-4 text-xs text-gray-400 max-w-[200px] truncate">
                          {item.tags.map((t) => t.notes).filter(Boolean).join("; ") || "—"}
                        </td>
                      </tr>
                    ))}
                    {taggedContacts.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-gray-400">
                          No tagged contacts found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {contactsTotal > 50 && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setContactPage((p) => Math.max(1, p - 1))}
                    disabled={contactPage <= 1}
                    className="text-sm text-blue-600 font-medium disabled:text-gray-300"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-400">Page {contactPage}</span>
                  <button
                    onClick={() => setContactPage((p) => p + 1)}
                    disabled={taggedContacts.length < 50}
                    className="text-sm text-blue-600 font-medium disabled:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Manage tags tab */}
          {activeTab === "manage" && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Campaign Survey Tags</h3>
              <SurveyTagManager campaignId={parseInt(selectedCampaign)} onTagsChange={() => loadSummary()} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
